import { inject, injectable } from 'inversify-props';
import { IHttpClient, ILogger, ISettings } from '../common';
import { Company, CompanyIndex, PagedResult, CompanySearchRequest, DeckCompany, SubjectAggregate } from '../../models';

/** Responsible for all service call operations to the Company end-points */
export interface ICompanyService {

	page(pageIndex: number, pageSize: number): Promise<PagedResult<Company>>;
	get(id: string): Promise<Company>;
	getMultiple(ids: string[]): Promise<Company[]>;
	autocomplete(crtieria: string): Promise<Company[]>;
	search(request: CompanySearchRequest): Promise<PagedResult<Company>>;
	index(): Promise<CompanyIndex[]>;
	save(company: Company): Promise<Company>;
	delete(id: string): Promise<any>;
	pageByDeck(deckId: string, pageIndex: number, pageSize: number): Promise<PagedResult<DeckCompany>>;
	listByDeck(deckId: string): Promise<DeckCompany[]>;
	listByProWithCount(proId: string, pageIndex: number, pageSize: number): Promise<PagedResult<SubjectAggregate<Company>>>;
	listByArtistWithCount(artistId: string, pageIndex: number, pageSize: number): Promise<PagedResult<SubjectAggregate<Company>>>;
}

/** Responsible for all service call operations to the Company end-points */
@injectable()
export class CompanyService implements ICompanyService {

	constructor(
		@inject('IHttpClient') private httpClient: IHttpClient,
		@inject('ILogger') private logger: ILogger,
		@inject('ISettings') private settings: ISettings) {}


	public page(pageIndex: number, pageSize: number): Promise<PagedResult<Company>> {
		const url = `${this.settings.baseUrl}${this.settings.companyBaseUrl}/${pageIndex}/${pageSize}`;
		return this.httpClient.get<PagedResult<Company>>(url);
	}

	public get(id: string): Promise<Company> {
		const url = `${this.settings.baseUrl}${this.settings.companyBaseUrl}/${id}`;
		return this.httpClient.get<Company>(url);
	}

	public getMultiple(ids: string[]): Promise<Company[]> {
		const url = `${this.settings.baseUrl}${this.settings.companyBaseUrl}/multiple/${ids.join(',')}`;
		return this.httpClient.get<Company[]>(url);
	}

	public autocomplete(criteria: string): Promise<Company[]> {
		const url = `${this.settings.baseUrl}${this.settings.companyBaseUrl}/autocomplete/${criteria}`;
		return this.httpClient.get<Company[]>(url);
	}

	public index(): Promise<CompanyIndex[]> {
		const url = `${this.settings.baseUrl}${this.settings.companyBaseUrl}/index`;
		return this.httpClient.get<CompanyIndex[]>(url);
	}

	public search(request: CompanySearchRequest): Promise<PagedResult<Company>> {
		const url = `${this.settings.baseUrl}${this.settings.companyBaseUrl}/search`;
		return this.httpClient.post<PagedResult<Company>>(url, request);
	}

	public save(company: Company): Promise<Company> {
		let url = `${this.settings.baseUrl}${this.settings.companyBaseUrl}`;

		const enabled = company.isEnabled == null ? null : (company.isEnabled.toString() === 'true') ? true : false;
		const popular = company.isPopular == null ? false : (company.isPopular.toString() === 'true') ? true : false;

		const payload = {
			name: company.name,
			headquarters: company.headquarters,
			aliases: company.aliases,
			startYear: (company.startYear != null) ? parseInt(company.startYear.toString(), null) : null,
			endYear: (company.endYear != null) ? parseInt(company.endYear.toString(), null) : null,
			aosId: (company.aosId != null) ? parseInt(company.aosId.toString(), null) : null,
			description: company.description,
			isEnabled: enabled,
			isPopular: popular,
			outOfBusiness: (company.endYear != null),
		};
		if (company.id) {
			url = `${url}/${company.id}`;
			return this.httpClient.put<Company>(url, payload);
		} else {
			return this.httpClient.post<Company>(url, payload);
		}
	}

	public delete(id: string): Promise<any> {
		const url = `${this.settings.baseUrl}${this.settings.companyBaseUrl}/${id}`;
		return this.httpClient.delete(url);
	}

	public listByDeck(deckId: string): Promise<DeckCompany[]> {
		const url = `${this.settings.baseUrl}${this.settings.deckBaseUrl}/${deckId}/companies`;
		return this.httpClient.get<DeckCompany[]>(url);
	}

	public pageByDeck(deckId: string, pageIndex: number, pageSize: number): Promise<PagedResult<DeckCompany>> {
		const url = `${this.settings.baseUrl}${this.settings.deckBaseUrl}/${deckId}/companies/${pageIndex}/${pageSize}`;
		return this.httpClient.get<PagedResult<DeckCompany>>(url);
	}

	public listByProWithCount(proId: string, pageIndex: number, pageSize: number): Promise<PagedResult<SubjectAggregate<Company>>> {
		const url = `${this.settings.baseUrl}${this.settings.proBaseUrl}/${proId}/companies/with-count/${pageIndex}/${pageSize}`;
		return this.httpClient.get<PagedResult<SubjectAggregate<Company>>>(url);
	}

	public listByArtistWithCount(artistId: string, pageIndex: number, pageSize: number): Promise<PagedResult<SubjectAggregate<Company>>> {
		const url = `${this.settings.baseUrl}${this.settings.artistBaseUrl}/${artistId}/companies/with-count/${pageIndex}/${pageSize}`;
		return this.httpClient.get<PagedResult<SubjectAggregate<Company>>>(url);
	}


}
